import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'

type SeoConfig = {
  title: string
  description: string
  robots: string
  canonicalPath: string
  ogType?: string
}

const PRIMARY_SITE_URL = 'https://marinaglen.co.za'
const DEFAULT_OG_IMAGE = `${PRIMARY_SITE_URL}/images/resort-overview.jpg`

const PUBLIC_ROUTE_SEO: Record<string, SeoConfig> = {
  '/booking-system': {
    title: 'Marina Glen Holiday Resort Booking | Marina Beach Accommodation',
    description: 'Book your Marina Glen Holiday Resort stay in Marina Beach, KwaZulu-Natal. Explore self-catering family accommodation, live availability, and direct booking rates.',
    robots: 'index,follow',
    canonicalPath: '/booking-system',
    ogType: 'website',
  },
  '/booking-system/rooms': {
    title: 'Marina Glen Units | 4-Sleeper and 6-Sleeper Self-Catering Stays',
    description: 'Compare Marina Glen Holiday Resort units, amenities, seasonal rates, and availability for 4-sleeper and 6-sleeper self-catering accommodation in Marina Beach.',
    robots: 'index,follow',
    canonicalPath: '/booking-system/rooms',
    ogType: 'website',
  },
  '/booking-system/booking': {
    title: 'Book Marina Glen Holiday Resort | Live Availability and Direct Rates',
    description: 'Reserve your Marina Glen Holiday Resort accommodation with live seasonal pricing, booking availability, and direct online reservations for Marina Beach.',
    robots: 'index,follow',
    canonicalPath: '/booking-system/booking',
    ogType: 'website',
  },
}

const PRIVATE_ROUTE_PATHS = new Set([
  '/booking-system/payment',
  '/booking-system/booking-form',
  '/booking-system/booking-preview',
  '/booking-system/login',
  '/booking-system/register',
  '/booking-system/reset-password',
  '/booking-system/dashboard',
  '/booking-system/my-bookings',
  '/booking-system/settings',
  '/booking-system/admin',
  '/booking-system/admin/seasonal-calendar',
])

const DEFAULT_PUBLIC_SEO: SeoConfig = {
  title: 'Marina Glen Holiday Resort | Marina Beach Holiday Accommodation',
  description: 'Discover Marina Glen Holiday Resort in Marina Beach, KwaZulu-Natal, with direct booking, family-friendly self-catering accommodation, and South Coast holiday stays.',
  robots: 'index,follow',
  canonicalPath: '/booking-system',
  ogType: 'website',
}

const upsertMetaTag = (attributeName: 'name' | 'property', attributeValue: string, content: string) => {
  let metaTag = document.head.querySelector(`meta[${attributeName}="${attributeValue}"]`) as HTMLMetaElement | null

  if (!metaTag) {
    metaTag = document.createElement('meta')
    metaTag.setAttribute(attributeName, attributeValue)
    document.head.appendChild(metaTag)
  }

  metaTag.setAttribute('content', content)
}

const upsertCanonicalLink = (href: string) => {
  let canonicalLink = document.head.querySelector('link[rel="canonical"]') as HTMLLinkElement | null

  if (!canonicalLink) {
    canonicalLink = document.createElement('link')
    canonicalLink.setAttribute('rel', 'canonical')
    document.head.appendChild(canonicalLink)
  }

  canonicalLink.setAttribute('href', href)
}

const getSeoConfig = (pathname: string): SeoConfig => {
  if (PRIVATE_ROUTE_PATHS.has(pathname)) {
    return {
      title: 'Marina Glen Holiday Resort Portal',
      description: 'Secure Marina Glen Holiday Resort portal access for bookings, guests, and administration.',
      robots: 'noindex,nofollow',
      canonicalPath: pathname,
      ogType: 'website',
    }
  }

  return PUBLIC_ROUTE_SEO[pathname] || DEFAULT_PUBLIC_SEO
}

export default function RouteSeo() {
  const location = useLocation()

  useEffect(() => {
    const seoConfig = getSeoConfig(location.pathname)
    const canonicalUrl = `${PRIMARY_SITE_URL}${seoConfig.canonicalPath}`

    document.title = seoConfig.title

    upsertMetaTag('name', 'description', seoConfig.description)
    upsertMetaTag('name', 'robots', seoConfig.robots)
    upsertMetaTag('property', 'og:title', seoConfig.title)
    upsertMetaTag('property', 'og:description', seoConfig.description)
    upsertMetaTag('property', 'og:url', canonicalUrl)
    upsertMetaTag('property', 'og:type', seoConfig.ogType || 'website')
    upsertMetaTag('property', 'og:image', DEFAULT_OG_IMAGE)
    upsertMetaTag('name', 'twitter:card', 'summary_large_image')
    upsertMetaTag('name', 'twitter:title', seoConfig.title)
    upsertMetaTag('name', 'twitter:description', seoConfig.description)
    upsertMetaTag('name', 'twitter:image', DEFAULT_OG_IMAGE)
    upsertCanonicalLink(canonicalUrl)
  }, [location.pathname])

  return null
}